import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import { RootState } from "features/config/store";
import { AttendanceStaticData } from "features/ms6/core/domain/DailyStaticData.model";
import {
  DailyStaticDataRepository,
  DailyStaticDataResponse,
  getUserStores6MSRepository,
  UserStores6MSResponse,
} from "features/ms6/data/ms6.repository";

export enum dailyStaticDataState {
  initial,
  inProgress,
  success,
  fail,
}

// Add store interface
interface Store {
  store_id: string;
  store_name: string;
}

interface InitialState {
  status: dailyStaticDataState;
  message: string;
  data: AttendanceStaticData | undefined;
  stores: Store[]; // Add stores array
  selectedStore: string; // Add selected store
}

const initialState: InitialState = {
  status: dailyStaticDataState.initial,
  message: "",
  data: undefined,
  stores: [], // Initialize empty stores array
  selectedStore: "", // Initialize empty selected store
};

// Add new thunk for fetching user stores
export const fetchUserStores = createAsyncThunk(
  "dailyStaticData/fetchUserStores",
  async (_: void, { rejectWithValue }) => {
    try {
      const response: UserStores6MSResponse =
        await getUserStores6MSRepository();
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

// Update existing thunk to accept store parameter
export const dailyStaticData = createAsyncThunk(
  "dailyStaticData",
  async (storeId: string | undefined, { rejectWithValue }) => {
    try {
      const response: DailyStaticDataResponse = await DailyStaticDataRepository(
        storeId
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

/* Main Slice */
export const dailyStaticDataSlice = createSlice({
  name: "dailyStaticData",
  initialState,
  reducers: {
    resetdailyStaticData: (state) => {
      state.status = dailyStaticDataState.initial;
      state.message = "";
    },
    // Add reducer to set selected store
    setSelectedStore: (state, action) => {
      state.selectedStore = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(dailyStaticData.pending, (state) => {
        state.status = dailyStaticDataState.inProgress;
      })
      .addCase(dailyStaticData.fulfilled, (state, action) => {
        if (action.payload) {
          const { message, data } = action.payload;
          state.status = dailyStaticDataState.success;
          state.message = message;
          state.data = data;
        }
      })
      .addCase(dailyStaticData.rejected, (state, action) => {
        state.status = dailyStaticDataState.fail;
        state.message = action.payload as string;
      })
      // Add cases for fetchUserStores
      .addCase(fetchUserStores.pending, (state) => {
        // Don't change the main status for store fetching
      })
      .addCase(fetchUserStores.fulfilled, (state, action) => {
        if (action.payload) {
          state.stores = action.payload.data;
          // Auto-select first store if only one available
          if (action.payload.data.length === 1) {
            state.selectedStore = action.payload.data[0].store_id;
          }
        }
      })
      .addCase(fetchUserStores.rejected, (state, action) => {
        state.message = action.payload as string;
      });
  },
});

export const selectDailyStaticData = (state: RootState) =>
  state.dailyStaticData;
export const { resetdailyStaticData, setSelectedStore } =
  dailyStaticDataSlice.actions;
export default dailyStaticDataSlice.reducer;
